import { useCallback, useEffect, useRef, useState } from "react";

import {
  AiInterviewerVersionSuggestionsStatus,
  GuideAnalysisResult,
  useAiInterviewerVersionSuggestionsQuery,
} from "../../graphql";
import { useFeatureFlagForCurrentOrImpersonatedUser } from "../../graphql/hooks/useFeatureFlag";
import { useAiInterviewerContext } from "./AiInterviewerContext";

type UseAnalyzeGuideResult = {
  analysisResult: GuideAnalysisResult | null;
  isPanelOpen: boolean;
  openPanel: () => void;
  closePanel: () => void;
  loading: boolean;
  error: string | null;
  isFeatureEnabled: boolean;
};

const POLL_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 300000; // 5 minutes

const useAnalyzeGuide = (): UseAnalyzeGuideResult => {
  const aiInterviewer = useAiInterviewerContext();
  const isFeatureEnabled = useFeatureFlagForCurrentOrImpersonatedUser(
    "ai_interviewer:suggestions:frontend"
  );

  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const pollStartTimeRef = useRef<number | null>(null);
  const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { data, loading, error, startPolling, stopPolling } =
    useAiInterviewerVersionSuggestionsQuery({
      variables: { aiInterviewerId: aiInterviewer.id },
      skip: !isFeatureEnabled,
      fetchPolicy: "network-only",
    });

  const status = data?.aiInterviewerVersionSuggestions?.status;
  const result = data?.aiInterviewerVersionSuggestions?.result ?? null;
  const errorMessage =
    data?.aiInterviewerVersionSuggestions?.errorMessage ?? null;

  // Manage polling based on status
  useEffect(() => {
    if (!isFeatureEnabled) {
      stopPolling();
      return;
    }

    const isPending =
      status === AiInterviewerVersionSuggestionsStatus.Pending ||
      status === AiInterviewerVersionSuggestionsStatus.InProgress;

    if (isPending && !timedOut) {
      // Start polling if not already
      if (pollStartTimeRef.current === null) {
        pollStartTimeRef.current = Date.now();
        // Set a timer to enforce timeout independently of status changes
        timeoutTimerRef.current = setTimeout(() => {
          setTimedOut(true);
          stopPolling();
        }, POLL_TIMEOUT_MS);
      }

      startPolling(POLL_INTERVAL_MS);
    } else {
      stopPolling();
      pollStartTimeRef.current = null;
      if (timeoutTimerRef.current) {
        clearTimeout(timeoutTimerRef.current);
        timeoutTimerRef.current = null;
      }
    }
  }, [status, isFeatureEnabled, timedOut, startPolling, stopPolling]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      stopPolling();
      if (timeoutTimerRef.current) {
        clearTimeout(timeoutTimerRef.current);
      }
    };
  }, [stopPolling]);

  const openPanel = useCallback(() => {
    setIsPanelOpen(true);
  }, []);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
  }, []);

  // Determine if we're in a loading state
  const isLoading =
    !timedOut &&
    (loading ||
      status === AiInterviewerVersionSuggestionsStatus.Pending ||
      status === AiInterviewerVersionSuggestionsStatus.InProgress);

  // Determine error state
  let displayError: string | null = null;
  if (error) {
    displayError = error.message;
  } else if (status === AiInterviewerVersionSuggestionsStatus.Failed) {
    displayError = errorMessage ?? "Analysis failed";
  } else if (timedOut) {
    displayError = "Analysis timed out. Please try again later.";
  }

  return {
    analysisResult: result,
    isPanelOpen,
    openPanel,
    closePanel,
    loading: isLoading,
    error: displayError,
    isFeatureEnabled,
  };
};

export default useAnalyzeGuide;
